// NOA, el chatbot determinista 🐢 — decisión de Patricio (22-08): nada de IA
// por ahora. Motor de intenciones por palabras clave que responde CALCULANDO
// sobre los datos vivos que la app ya tiene (brief, hoteles, alertas, premio).
// Ventajas: instantáneo, coste cero, y jamás inventa una cifra. Si algún día
// entra la IA (fase 2), este motor queda como primera línea y la IA como
// respaldo para lo que no matchee.

import type { Alerta } from '@/constants/data';
import { api, type Brief, type HotelRow, type IaReply, type Premio } from '@/api/client';
import { fmt, fmtDec, fmtE, fmtK, fmtM, pct1 } from '@/lib/format';

export interface NoaCtx {
  /* Para saludar por su nombre: Noa habla con una persona, no con un usuario. */
  nombre?: string | null;
  brief: Brief | null;
  hoteles: HotelRow[];
  alertas: Alerta[];
  premio: Premio | null;
}

const MESES = ['', 'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const PMS_LBL: Record<string, string> = { noray: 'Noray', onasystem: 'OnaSystem', navision: 'Navision', avalon: 'Avalon' };

const norm = (s: string) =>
  s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9ñ\s]/g, ' ').replace(/\s+/g, ' ').trim();

const hay = (q: string, ...palabras: string[]) => palabras.some((p) => q.includes(p));
/* Ayer en hora española: el corte del día del resto de la app. */
const _ayerIso = () => {
  const hoy = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Madrid', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
  const d = new Date(hoy + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
};
const ddmm = (iso?: string) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : '');

/* ══════════════════════════════════════════════════════════════════════════
   EL HILO DE LA CONVERSACIÓN.
   Patricio: «"cuánto facturó en agosto Cala Pi con respecto al 2025" da la
   respuesta; y si después preguntamos "¿y en Nerja?", que se entienda que la
   pregunta es en base a la anterior. Esto aplicaría para TODAS las preguntas».

   🔴 Y esa última frase es la que decide el diseño. Se podía hacer que cada
   habilidad recordase su propio contexto, y habría habido que tocarlas todas —y
   acordarse de tocar también la siguiente que escriba. En vez de eso, el
   seguimiento se resuelve ANTES: se REESCRIBE la pregunta corta hasta convertirla
   en la anterior con el dato nuevo dentro, y luego se procesa como cualquier otra.
   Una sola pieza, y funciona para todas — incluidas las que aún no existen.

   O sea: «¿y en Nerja?» se convierte en «cuanto facturo en agosto nerja con
   respecto al 2025» y sigue su camino normal.

   Se recuerda SOLO la última pregunta que se supo contestar: si Noa no entendió,
   no hay hilo del que tirar y es mejor que la siguiente empiece limpia. Y vive en
   el módulo a propósito, no en el estado de la pantalla: el hilo es de la
   conversación, no de un componente que se puede volver a montar.
   ══════════════════════════════════════════════════════════════════════════ */
let _hilo: { q: string; hotel: string | null } | null = null;

/** Corta el hilo a mano. Existe para un futuro botón de «nueva conversación»; hoy
    el hilo se invalida solo (ver abajo), así que nadie tiene que acordarse. */
export function noaOlvida() { _hilo = null; }

/* 🔴 EL HILO CADUCA SI CAMBIA EL ALCANCE. Patricio se cambia el rol para probar
   (DG, REG, DH), y con el rol cambia la lista de hoteles que ve. Un hilo que
   hablaba de un hotel que ya no está en su alcance contestaría sobre algo que no
   debería ver, o fallaría raro. Se comprueba en cada pregunta y no hace falta que
   nadie llame a nada. */
function _hiloSigueValido(hoteles: HotelRow[]): boolean {
  if (!_hilo) return false;
  if (!_hilo.hotel) return true;
  return hoteles.some((h) => norm(h.n.replace(/^ona /i, '')) === _hilo!.hotel);
}

/* ¿Es esto un seguimiento? Tiene que traer un dato NUEVO y poco más: «y en
   Nerja», «¿y Mirage?», «y en julio». Si la pregunta se sostiene sola, no se
   toca — reescribir una pregunta completa es la forma de contestar otra cosa. */
function reescribeSeguimiento(q: string, hoteles: HotelRow[]): string | null {
  if (!_hilo) return null;
  if (!_hiloSigueValido(hoteles)) { _hilo = null; return null; }
  const palabras = q.split(' ').filter(Boolean);
  if (palabras.length > 5) return null;

  const nuevoHotel = buscaHotel(q, hoteles);
  const nuevoMes = MESES.findIndex((m) => m && q.includes(m));
  if (!nuevoHotel && nuevoMes <= 0) return null;

  /* Si trae un verbo o una métrica, es una pregunta nueva que casualmente es
     corta («cuanto facturo nerja»), y se deja pasar tal cual. */
  if (hay(q, 'cuanto', 'como', 'quien', 'cual', 'donde', 'dame', 'ocupacion', 'adr',
          'budget', 'presupuesto', 'director', 'direccion', 'pagaran', 'factur')) return null;

  let nueva = _hilo.q;
  if (nuevoHotel) {
    const nuevoNom = norm(nuevoHotel.n.replace(/^ona /i, ''));
    if (_hilo.hotel && nueva.includes(_hilo.hotel)) {
      nueva = nueva.split(_hilo.hotel).join(nuevoNom);
    } else {
      nueva = `${nueva} ${nuevoNom}`;
    }
  }
  if (nuevoMes > 0) {
    const viejo = MESES.findIndex((m) => m && nueva.includes(m));
    nueva = viejo > 0 ? nueva.split(MESES[viejo]).join(MESES[nuevoMes]) : `${nueva} ${MESES[nuevoMes]}`;
  }
  return nueva === _hilo.q ? null : nueva;
}

/* Detecta un hotel en la pregunta (nombre normalizado, sin el prefijo Ona). */
/* Sin espacios ni signos: «cala pi» y «calapi» son la misma cosa. El dictado
   junta y separa palabras a su antojo, y esto lo absorbe sin listas de excepciones. */
const compacta = (s: string) => norm(s).replace(/[^a-z0-9ñ]/g, '');

function buscaHotel(q: string, hoteles: HotelRow[]): HotelRow | null {
  let mejor: HotelRow | null = null;
  let mejorLen = 0;
  const qC = compacta(q);
  for (const h of hoteles) {
    const nombre = norm(h.n.replace(/^ona /i, ''));
    /* 🔴 TROZOS DE 5, NO DE 4 (27-08). Con 4, «cala» era un trozo válido y lo
       tienen Cala Pi y Cala d'Or: quien dijera «cala» algo se llevaba el hotel que
       apareciese primero en la lista. Contestar del hotel EQUIVOCADO con toda
       seguridad es peor que decir que no se ha entendido. Con 5, «cala» deja de
       valer por sí solo y esos dos hoteles solo se reconocen por el nombre
       entero — que es lo correcto, porque es lo que los distingue. */
    const trozos = [nombre, ...nombre.split(' ').filter((t) => t.length >= 5)];
    for (const t of trozos) {
      if (t.length > mejorLen && q.includes(t)) { mejor = h; mejorLen = t.length; }
    }
    /* Y la forma compactada, que es la que salva a «Cala Pi» cuando el dictado
       escribe «calapi» o «cala pie»: 6 letras pesan más que cualquier trozo. */
    const nC = compacta(nombre);
    if (nC.length > mejorLen && qC.includes(nC)) { mejor = h; mejorLen = nC.length; }
  }
  return mejor;
}

const filasPorPms = (
  items: Array<{ pms: string; v: string; extra?: string }>,
): IaReply['rows'] => items.map((x) => [`${PMS_LBL[x.pms] ?? x.pms}${x.extra ? ` · ${x.extra}` : ''}`, x.v, 'flat']);

/* Puntos de venta que Noa reconoce → término de búsqueda contra /pdv.
   El regex corre sobre la pregunta normalizada (sin acentos, minúsculas). */
const PDV_KEYS: Array<{ re: RegExp; q: string; label: string }> = [
  { re: /supermercad\w*|\bsuper\b|\bsupers\b/, q: 'super', label: 'Supermercados' },
  { re: /bar(es)? piscina|pool ?bar|\bbar(es)?\b/, q: 'bar', label: 'Bares' },
  { re: /restaurant\w*/, q: 'restaur', label: 'Restaurantes' },
  { re: /desayun\w*/, q: 'desayun', label: 'Desayunos' },
  { re: /\bcenas?\b/, q: 'cena', label: 'Cenas' },
  { re: /\bcomidas?\b|almuerzo/, q: 'comida', label: 'Comidas' },
  { re: /parking|aparcamiento|garaje/, q: 'parking', label: 'Parking' },
  { re: /\bspa\b|masaje/, q: 'spa', label: 'Spa' },
  { re: /bisuteria|tienda/, q: 'bisuteria', label: 'Tienda / bisutería' },
];

const SIN_FOLIOS: IaReply = {
  txt: 'Los folios vivos no me responden ahora mismo — puede ser cobertura o que estés en modo demo. En cuanto vuelvan, te doy súper, bares, restaurantes y el día en curso al minuto de la última sync.',
  rows: [], foot: '',
};

export async function noaResponde(pregunta: string, ctx: NoaCtx): Promise<IaReply> {
  /* 🔴 EL HILO GUARDA LA PREGUNTA EFECTIVA, NO LA QUE SE TECLEÓ. Si guardara la
     cruda, un segundo seguimiento partiría de «y en nerja» —que no dice qué se
     preguntaba— y la cadena se rompería en el segundo salto. Guardando la
     reescrita, «...y en Nerja» → «...y en Mirage» sigue funcionando indefinidamente. */
  const cruda = norm(pregunta);
  const efectiva = reescribeSeguimiento(cruda, ctx.hoteles) ?? cruda;
  const r = await _responde(efectiva, ctx);
  /* Solo se recuerda lo que se supo contestar: sin `intencion`, Noa no entendió y
     un hilo colgado de una pregunta fallida arrastra el error a la siguiente. */
  if (r.intencion && !['saludo', 'cortesia', 'quien-soy'].includes(r.intencion)) {
    const h = buscaHotel(efectiva, ctx.hoteles);
    const nom = h ? norm(h.n.replace(/^ona /i, '')) : null;
    _hilo = { q: efectiva, hotel: nom && efectiva.includes(nom) ? nom : null };
  }
  return r;
}

async function _responde(q: string, ctx: NoaCtx): Promise<IaReply> {
  const { brief, hoteles, alertas, premio } = ctx;
  const sinDatos: IaReply = { txt: 'Todavía estoy cargando los datos — dame un segundo y vuelve a preguntar.', rows: [], foot: '' };

  /* ── saludos y cortesía ─────────────────────────────────────────────── */
  if (hay(q, 'hola', 'buenos dias', 'buenas tardes', 'buenas noches', 'hey')) {
    return {
      txt: `${ctx.nombre ? `¡Hola, ${ctx.nombre}!` : '¡Hola!'} Soy Noa. Pregúntame por la producción, `
         + 'la ocupación, el ADR, un hotel concreto, las alertas o los Premios Noa.',
      rows: [], foot: 'Prueba: «¿cómo va ' + (hoteles[0]?.n.replace(/^Ona /i, '') ?? 'Don Juan') + '?»',
      intencion: 'saludo',
    };
  }
  /* Quién soy — mejor calculado que enseñado: la app ya sabe con quién habla.
     Se responde aquí para que nadie tenga que enseñárselo. */
  if (hay(q, 'como me llamo', 'cual es mi nombre', 'quien soy', 'mi nombre',
          'como se llama mi usuario', 'que usuario soy')) {
    return {
      txt: ctx.nombre
        ? `Te llamas ${ctx.nombre} 🙂`
        : 'No sé con quién estoy hablando: parece que no hay sesión iniciada.',
      rows: [], intencion: 'quien-soy',
      foot: 'Lo sé por tu cuenta de Ona, la misma con la que has entrado.',
    };
  }
  if (hay(q, 'gracias', 'genial', 'perfecto', 'crack')) {
    return { txt: '¡De nada! Aquí estoy, latiendo con el grupo 🌊', rows: [], foot: '', intencion: 'cortesia' };
  }
  if (hay(q, 'que sabes', 'ayuda', 'help', 'que puedo preguntar', 'que haces')) {
    return {
      txt: 'Pregúntame lo que quieras de esto:',
      rows: [
        ['Ahora mismo', '«¿cuánto llevamos hoy?» — folios vivos', 'flat'],
        ['Puntos de venta', 'súper, bares, restaurantes, desayunos…', 'flat'],
        ['Producción', 'de ayer y del mes, del grupo o por PMS', 'flat'],
        ['Por hotel', '«¿cómo va Valle Romano?»', 'flat'],
        ['Ocupación y ADR', 'de hoy / del mes, por PMS', 'flat'],
        ['Alertas y ranking', 'overbooking, mejores y peores', 'flat'],
        ['Premios Noa', 'el podio del mes publicado', 'flat'],
        ['Contra presupuesto', '«¿cómo vamos con el budget en Mirage?»', 'flat'],
        ['Quién y dónde', '«¿quién es el director de Cala Pi?» · su dirección', 'flat'],
        ['Cobros por venir', '«¿cuánto pagarán los clientes en 30, 60 o 90 días?»', 'flat'],
        ['Contra el año pasado', '«¿cuánto facturó en agosto Cala Pi respecto a 2025?»', 'flat'],
        ['Y sigo el hilo', 'después de una respuesta, basta con «¿y en Nerja?»', 'flat'],
      ],
      foot: 'Siempre con el dato real de Hotelos — y lo que no sepa, te lo digo sin rodeos.',
    };
  }

  /* ── puntos de venta (súper, bares, restaurantes…) — folios vivos ───── */
  const pdvKey = PDV_KEYS.find((k) => k.re.test(q));
  if (pdvKey) {
    /* 🔴 SI NOMBRA UN HOTEL, ES DE ESE HOTEL. Antes se contestaba con el súper
       de los 45 y se añadía una línea con el del hotel al final: la cifra
       grande de arriba era del grupo y se leía como del hotel. Ahora el hotel
       acota la consulta entera. */
    const hotelPdv = buscaHotel(q, hoteles);
    const r = await api.pdv(pdvKey.q, hotelPdv?.id ?? null);
    if (!r) return SIN_FOLIOS;
    if (!r.encontrado) {
      return {
        txt: `No veo un punto de venta que suene a «${pdvKey.label.toLowerCase()}». Estos son los que más mueven este mes:`,
        rows: (r.disponibles ?? []).slice(0, 8).map((d): IaReply['rows'][0] => [d.punto, fmtE(d.mes), 'flat']),
        foot: 'Dime uno y te doy el hoy, el ayer y el mes. Solo hoteles Noray (folios).',
      };
    }
    const rows: IaReply['rows'] = [
      [`Hoy${r.ultSync ? ` (hasta las ${r.ultSync})` : ''}`, fmtE(r.hoy ?? 0), 'flat'],
      ['Ayer', fmtE(r.ayer ?? 0), 'flat'],
      ['Mes acumulado', fmtE(r.mes ?? 0), 'good'],
    ];
    if (!hotelPdv) {
      (r.porHotel ?? []).slice(0, 3).forEach((h, i) => rows.push([`${i + 1}. ${h.n.replace(/^Ona /i, '')}`, fmtE(h.mes), 'flat']));
    }
    const incluye = (r.puntos ?? []).map((p) => p.punto).slice(0, 4).join(', ');
    return {
      intencion: 'punto-de-venta',
      txt: hotelPdv
        ? `${pdvKey.label} de ${hotelPdv.n.replace(/^Ona /i, '')} — así van:`
        : `${pdvKey.label} — así van:`,
      rows,
      foot: `${incluye ? `Incluye: ${incluye}. ` : ''}${hotelPdv ? 'Solo este hotel. ' : ''}`
          + 'Folios de Noray, neto sin IVA; el día en curso es provisional.',
    };
  }

  /* ── «¿cuánto llevamos HOY?» — el día en curso, folios vivos ────────── */
  const pideHoy =
    hay(q, 'ahora mismo', 'hasta ahora', 'tiempo real', 'en vivo', 'en este momento', 'va el dia', 'dia de hoy') ||
    (q.includes('hoy') && hay(q, 'cuanto', 'llevamos', 'produc', 'factur', 'vendi', 'como va')) ||
    q === 'hoy';
  if (pideHoy && !hay(q, 'ocupacion', 'adr', 'lleno')) {
    const r = await api.hoy();
    if (!r) return SIN_FOLIOS;
    if (!r.total) {
      return {
        txt: `Los folios de hoy (${ddmm(r.fecha)}) aún no se han volcado${r.ultSync ? ` — última sync a las ${r.ultSync}` : ''}. En cuanto corra la sincronización te digo cómo va el día.`,
        rows: [], foot: 'Mientras tanto, «¿cuánto produjo el grupo ayer?» sí está cerrado y validado.',
      };
    }
    return {
      txt: `Hoy ${ddmm(r.fecha)}, hasta las ${r.ultSync ?? '—'}, llevamos ${fmtE(r.total)} netos en folios (provisional).`,
      rows: [
        ...r.hoteles.slice(0, 3).map((h, i): IaReply['rows'][0] => [`${i + 1}. ${h.n.replace(/^Ona /i, '')}`, fmtE(h.imp), 'good']),
        ...r.puntos.slice(0, 3).map((p): IaReply['rows'][0] => [p.punto, fmtE(p.imp), 'flat']),
      ],
      foot: `${r.hotelesConDatos} hoteles Noray con folios del día. La cifra oficial llega con el volcado contable de mañana.`,
    };
  }

  /* ══════════════════════════════════════════════════════════════════════
     LO QUE PATRICIO DIJO QUE LE VA A PREGUNTAR EL CEO (25-08-2026).
     Su lista, literal: «cómo vamos con respecto al budget en {hotel}», «cuánto
     facturó {punto de venta}», «cuánto facturó {hotel}», «quién es el director
     de {hotel}», «cómo se llama el director de {hotel}», «cuál es la dirección
     del {hotel}», «cuánto pagarán los clientes en los próximos 30 / 60 / 90
     días». Van ANTES del resumen genérico del hotel, que si no se las come.
     ══════════════════════════════════════════════════════════════════════ */

  /* ── «¿cómo vamos con respecto al budget en X?» ──────────────────────
     🔴 ERA EL AGUJERO GORDO. Preguntabas si un hotel llega al presupuesto y
     contestaba la PRODUCCIÓN, callándose el presupuesto — o sea, contestaba otra
     cosa. El dato existe: sale de `pl_budget_real`, que es de donde lo lee
     Hotelos, y viaja ya calculado en /comercial. */
  const hotelBud = buscaHotel(q, hoteles);
  if (hotelBud && hay(q, 'budget', 'presupuesto', 'objetivo', 'llegamos', 'vamos con respecto',
                      'cumpliendo', 'va a llegar', 'llega al')) {
    const nom = hotelBud.n.replace(/^Ona /i, '');
    /* Un hotel fuera de Control de Gestión NO tiene presupuesto mensual, y eso
       hay que decirlo con su razón: no es un fallo, es que no hace el cierre
       hotelero. Son 14 de los 53 y facturan 4,2 M€. */
    if (hotelBud.cg === false) {
      return {
        intencion: 'budget-hotel',
        txt: `${nom} no tiene presupuesto contra el que compararse.`,
        rows: [['Producción del mes', fmtE(hotelBud.mes), 'flat']],
        foot: 'No participa del cierre hotelero de Control de Gestión, así que no hay budget '
            + 'mensual cargado. Los 39 que sí lo hacen son los que puedo comparar.',
      };
    }
    const rc = await api.comercial();
    if (!rc.ok) return { txt: rc.motivo, rows: [], foot: '' };
    const h = rc.dato.hoteles.find((x) => x.id === hotelBud.id);
    if (!h || !h.mes) {
      return {
        intencion: 'budget-hotel',
        txt: `No tengo presupuesto cargado de ${nom}, así que no puedo decirte si va a llegar.`,
        rows: [['Producción del mes', fmtE(hotelBud.mes), 'flat']],
        foot: 'El presupuesto en euros sale de pl_budget_real; si este hotel no está, es que no se ha cargado.',
      };
    }
    const m = h.mes;
    const vaBien = m.pctReal >= m.pctMes;
    const rows: IaReply['rows'] = [
      ['Conseguido', fmtE(m.conseguido), vaBien ? 'good' : 'bad'],
      ['Presupuesto del mes', fmtE(m.presupuesto), 'flat'],
      ['Va al', `${m.pctReal}% con el ${m.pctMes}% del mes`, vaBien ? 'good' : 'bad'],
    ];
    if (m.faltan != null && m.porDia != null) {
      rows.push(['Le faltan', `${fmtE(m.faltan)} en ${m.diasRestantes} día${m.diasRestantes === 1 ? '' : 's'}`, 'flat']);
      rows.push(['O sea', `${fmtE(m.porDia)} al día`, 'flat']);
    }
    if (h.anio) rows.push(['En el año', `${h.anio.pct}% con el ${h.anio.pctAnio}% del año`, h.anio.pct >= h.anio.pctAnio ? 'good' : 'bad']);
    return {
      intencion: 'budget-hotel',
      txt: vaBien
        ? `${nom} va POR DELANTE del presupuesto: ${m.pctReal}% conseguido con el ${m.pctMes}% del mes transcurrido.`
        : `${nom} va POR DETRÁS del presupuesto: ${m.pctReal}% conseguido con el ${m.pctMes}% del mes transcurrido.`,
      rows,
      foot: 'Presupuesto de pl_budget_real, el mismo que lee Hotelos. Producción del mes en curso, '
          + 'neto sin IVA y provisional hasta el cierre.',
    };
  }

  /* ── «¿cuánto facturó en agosto X con respecto al 2025?» ─────────────
     🔴 LA VENTANA ES LA MITAD DE LA RESPUESTA. Hoy es día 25: «agosto» de este
     año son 24 días y el de 2025 fueron 31. Comparados a pelo pintan un desplome
     del 23 % que no ha ocurrido, y eso delante del CEO es peor que no enseñarlo.
     El servidor compara los MISMOS días en los dos años y aquí se DICE. */
  const hotelAnio = buscaHotel(q, hoteles);
  const pideAnterior = hay(q, 'respecto al 2025', 'respecto a 2025', 'contra 2025', 'vs 2025',
                           'ano anterior', 'año anterior', 'anio anterior', 'mismo mes del ano',
                           'el ano pasado', 'año pasado', 'ano pasado', 'interanual', '2025');
  if (hotelAnio?.id != null && pideAnterior) {
    const mesPedido = MESES.findIndex((m) => m && q.includes(m));
    const r = await api.comparaAnio(hotelAnio.id, mesPedido > 0 ? mesPedido : undefined);
    if (!r.ok) return { txt: r.motivo, rows: [], foot: '' };
    const d = r.dato;
    const nom = d.hotel.replace(/^Ona /i, '');
    const mesTxt = MESES[d.mes] ?? '';

    if (!d.hayAnterior) {
      return {
        intencion: 'compara-anio',
        txt: `De ${mesTxt} de ${d.anioAnterior} no tengo producción de ${nom}, así que no puedo compararlo.`,
        rows: [[`${mesTxt} ${d.anio}`, fmtE(d.total), 'flat']],
        foot: d.avisos.join(' '),
      };
    }
    const sube = (d.pct ?? 0) >= 0;
    const rows: IaReply['rows'] = [
      [`${mesTxt} ${d.anio}`, fmtE(d.total), sube ? 'good' : 'bad'],
      [`${mesTxt} ${d.anioAnterior}`, fmtE(d.totalAnterior), 'flat'],
      ['Diferencia', `${sube ? '+' : ''}${fmtE(d.delta ?? 0)}`, sube ? 'good' : 'bad'],
    ];
    return {
      intencion: 'compara-anio',
      txt: `${nom} lleva ${fmtE(d.total)} en ${mesTxt} y ${sube ? 'sube' : 'baja'} un `
         + `${fmtDec(Math.abs(d.pct ?? 0))}% sobre ${mesTxt} de ${d.anioAnterior}`
         + `${d.enCurso ? ` (mismos días en los dos años: del 1 al ${d.ventana.dias})` : ''}.`,
      rows,
      foot: `${d.avisos.join(' ')} ${d.nota}`.trim(),
    };
  }

  /* ── «¿quién es el director de X?» · «¿cuál es la dirección de X?» ───
     Dos preguntas distintas con la misma fuente, así que un solo bloque. Ojo al
     castellano: «dirección» es la calle Y es el cargo, y «director» resuelve la
     ambigüedad — se mira primero si pregunta por una persona. */
  const hotelFicha = buscaHotel(q, hoteles);
  const pideQuien = hay(q, 'director', 'directora', 'quien dirige', 'quien lleva', 'quien esta al frente', 'regional');
  const pideDonde = hay(q, 'direccion', 'donde esta', 'donde queda', 'ubicacion', 'domicilio', 'calle', 'codigo postal');
  if (hotelFicha?.id != null && (pideQuien || pideDonde)) {
    /* 🔴 «¿QUIÉN ERA EL DIRECTOR EN JULIO?». Los directores viven en Premios Noa
       con vigencias (`desde`/`hasta`), justo para que un relevo no reescriba el
       pasado. Así que si la pregunta nombra un mes, se resuelve al ÚLTIMO DÍA de
       ese mes — la misma regla que usa el premio— y no a hoy. Sin mes, hoy.
       Se elige el año de forma que el mes nombrado no caiga en el futuro: en
       agosto, «en noviembre» es el noviembre pasado, no el que viene. */
    const mesNombrado = MESES.findIndex((m) => m && q.includes(m));
    let fechaFicha: string | undefined;
    if (pideQuien && mesNombrado > 0) {
      const hoyD = new Date();
      const anio = mesNombrado - 1 > hoyD.getMonth() ? hoyD.getFullYear() - 1 : hoyD.getFullYear();
      const fin = new Date(Date.UTC(anio, mesNombrado, 0));
      fechaFicha = fin.toISOString().slice(0, 10);
    }
    const rf = await api.fichaHotel(hotelFicha.id, fechaFicha);
    if (!rf.ok) return { txt: rf.motivo, rows: [], foot: '' };
    const d = rf.dato;
    const nom = d.nombre.replace(/^Ona /i, '');

    if (pideQuien) {
      if (!d.alcanceLegible) {
        return { intencion: 'director-hotel', txt: d.nota, rows: [], foot: '' };
      }
      if (!d.directores.length && !d.regionales.length) {
        return {
          intencion: 'director-hotel',
          txt: `No hay nadie asignado a ${nom} con rol de dirección en Hotelos.`,
          rows: [], foot: d.nota,
        };
      }
      const rows: IaReply['rows'] = [];
      if (d.declarado && d.director) {
        rows.push(['Director', d.director, 'good']);
        if (d.desde) rows.push(['Desde', ddmm(d.desde) + (d.hasta ? ` hasta ${ddmm(d.hasta)}` : ' · en vigor'), 'flat']);
      } else if (d.mostrar?.length) {
        /* Sin declarar: se enseña el nombre a mostrar completo, que ya lleva el
           cargo («Amparo Sequi – Dirección Cala Pi»), y se avisa de que falta
           declararlo — sin declarar, su Tortugona no tendría a quién acreditarse. */
        d.mostrar.forEach((x) => rows.push(['Directorio', x, 'flat']));
      } else {
        d.directores.forEach((x) => rows.push(['Dirección del hotel', x, 'good']));
      }
      if (d.puesto) rows.push(['Puesto', d.puesto, 'flat']);
      /* Los anteriores, que es lo que se quiere saber cuando alguien se acaba de
         ir: «¿y quién estaba antes?». Solo los que ya terminaron. */
      (d.historial ?? []).filter((h) => !h.vigente).slice(0, 3).forEach((h) => rows.push([
        `Antes${h.desde ? ` (${ddmm(h.desde)}–${h.hasta ? ddmm(h.hasta) : '…'})` : ''}`,
        h.quien, 'flat',
      ]));
      d.regionales.forEach((x) => rows.push(['Regional', x, 'flat']));
      if (d.zona) rows.push(['Zona', d.zona, 'flat']);
      const cuando = fechaFicha ? ` el ${ddmm(fechaFicha)}` : '';
      return {
        intencion: 'director-hotel',
        txt: d.director
          ? (fechaFicha
            ? `El director de ${nom}${cuando} era ${d.director}.`
            : `El director de ${nom} es ${d.director}.`)
          : `${nom} no tiene director${cuando ? ` declarado${cuando}` : ' de hotel asignado'}.`,
        rows,
        foot: `${d.nota}${d.via ? ` (vía ${d.via})` : ''}`,
      };
    }

    const rows: IaReply['rows'] = [];
    if (d.direccion) rows.push(['Dirección', d.direccion, 'flat']);
    if (d.habitaciones) rows.push(['Habitaciones', fmt(d.habitaciones), 'flat']);
    if (d.zona) rows.push(['Zona', d.zona, 'flat']);
    if (d.director) rows.push(['Director', d.director, 'flat']);
    return {
      intencion: 'direccion-hotel',
      txt: d.direccion ? `${nom} está en ${d.direccion}.` : `No tengo la dirección de ${nom} en Hotelos.`,
      rows,
      foot: d.direccion
        ? 'De la ficha del hotel en Hotelos.'
        : 'La dirección se rellena en Hotelos › Configuración › Hoteles; si está vacía, no la puedo inventar.',
    };
  }

  /* ── «¿cuánto pagarán los clientes en los próximos 30 / 60 / 90 días?» ──
     Son ventanas ACUMULADAS: la de 60 incluye los primeros 30. Se dice, porque
     si no alguien va a sumar las tres y le va a salir el triple. */
  if (hay(q, 'pagaran', 'pagaran los clientes', 'van a pagar', 'cobraremos', 'vamos a cobrar',
          'cobros previstos', 'que vence', 'vencimientos')) {
    const dias = /\b(90|60|30)\b/.exec(q)?.[1];
    const ra = await api.ageing();
    if (!ra.ok) return { txt: ra.motivo, rows: [], foot: '' };
    const cobros = ra.dato.cobros ?? (ra.dato.prox30 ? [ra.dato.prox30] : []);
    if (!cobros.length) {
      return {
        txt: 'Todavía no tengo las ventanas de cobro. Actualiza el servidor y te las doy.',
        rows: [], foot: '',
      };
    }
    const elegida = dias ? cobros.find((c) => c.dias === Number(dias)) : null;

    if (elegida) {
      const rows: IaReply['rows'] = elegida.top.slice(0, 6)
        .map((c): IaReply['rows'][0] => [c.n, fmtE(c.imp), 'flat']);
      return {
        intencion: 'cobros-ventana',
        txt: `En los próximos ${dias} días vencen ${fmtE(elegida.total)} de ${fmt(elegida.clientes)} clientes, `
           + `hasta el ${ddmm(elegida.hasta)}.`,
        rows,
        foot: (Math.abs(elegida.neto - elegida.total) >= 1
          ? `Hay ${fmtE(elegida.total - elegida.neto)} en cobros a cuenta dentro de la ventana, así que el neto son ${fmtE(elegida.neto)}. `
          : '')
          + 'Cartera abierta de Business Central. Aún NO vencido: esto es lo que va a entrar, no lo que se debe.',
      };
    }

    /* Sin número en la pregunta, las tres: es más útil que elegir por él. */
    return {
      intencion: 'cobros-ventana',
      txt: 'Esto es lo que tienen que pagarte los clientes, aún sin vencer:',
      rows: cobros.map((c): IaReply['rows'][0] => [
        `Próximos ${c.dias ?? 30} días`,
        `${fmtE(c.total)} · ${fmt(c.clientes)} clientes`,
        'flat',
      ]),
      foot: 'Ventanas ACUMULADAS: la de 60 incluye los primeros 30, y la de 90 incluye las dos. '
          + 'No se suman. Cartera abierta de Business Central, aún no vencido.',
    };
  }

  /* ── un hotel: producción de un día por cuenta ──────────────────────
     «¿cómo fue la producción de ayer en Mirage?» — el mismo desglose que
     enseña PMS › Noray › Producción al pinchar un día, por cuenta contable.
     Va ANTES del resumen del hotel porque es más específico. */
  const hotelProd = buscaHotel(q, hoteles);
  if (hotelProd?.id != null && hay(q, 'produccion', 'produjo', 'cargos', 'conceptos', 'desglose contable')) {
    const ayer = hay(q, 'hoy') ? undefined : _ayerIso();
    const r = await api.produccionDia(hotelProd.id, ayer);
    if (!r.ok) return { txt: r.motivo, rows: [], foot: '' };
    const d = r.dato;
    if (!d.hayDatos) {
      return {
        txt: `No hay cargos de ${d.hotel} el ${ddmm(d.fecha)}.`,
        rows: [], foot: d.ultimoConDatos
          ? `El último día con producción es el ${ddmm(d.ultimoConDatos)} — pídemelo y te lo cuento.`
          : 'Este hotel no tiene cargos en el espejo de Noray.',
      };
    }
    return {
      intencion: 'produccion-dia-hotel',
      txt: `${d.hotel} · producción del ${ddmm(d.fecha)}:`,
      rows: [
        ['Total del día', fmtE(d.total), 'good'],
        ['Con IVA', fmtE(d.totalBruto), 'flat'],
        ['Cargos', fmt(d.uds), 'flat'],
      ],
      secciones: d.grupos.map((g) => ({
        t: `${g.cuenta} · ${g.nombre}`,
        sub: `${fmt(g.uds)} uds`,
        filas: g.lineas.map((l): IaReply['rows'][0] => [l.concepto, fmtE(l.neto), 'flat']),
        total: ['', fmtE(g.neto)] as [string, string],
      })),
      foot: 'Cargos de Noray por cuenta contable (el mismo mapeo del Cuadre). Neto sin IVA.',
    };
  }

  /* ── hotel concreto (antes que las preguntas de grupo) ──────────────── */
  const hotel = buscaHotel(q, hoteles);
  if (hotel && !hay(q, 'grupo', 'todos')) {
    const rank = hoteles.findIndex((h) => h === hotel) + 1;
    const total = hoteles.reduce((a, h) => a + h.mes, 0);
    const rows: IaReply['rows'] = [
      ['Producción del mes', fmtK(hotel.mes), 'flat'],
      hotel.d != null
        ? [`Ayer${hotel.fecha ? ` (${ddmm(hotel.fecha)})` : ''}`, fmtE(hotel.d), 'flat'] as IaReply['rows'][0]
        // Falta el volcado de ese día: se dice, y se enseña el último que sí está.
        : [`Último día con producción (${ddmm(hotel.dFecha ?? undefined)})`,
           hotel.dUlt != null ? fmtE(hotel.dUlt) : '—', 'bad'] as IaReply['rows'][0],
      ['Ocupación hoy', hotel.occ != null ? pct1(hotel.occ) : '—', hotel.occ != null && hotel.occ > 100.4 ? 'bad' : 'flat'],
      ['ADR del mes', hotel.adr != null ? `${fmtDec(hotel.adr)} €` : '—', 'flat'],
      ['Posición', `nº ${rank} de ${hoteles.length} · ${total > 0 ? fmtDec((hotel.mes / total) * 100) : '—'}% del grupo`, 'flat'],
    ];
    if (hotel.pms === 'noray') {
      // toque de «ahora mismo»: lo que llevan sus folios del día en curso
      const hv = await api.hoy();
      const hoyRow = hv?.hoteles.find((x) => x.id === hotel.id || x.n === hotel.n);
      if (hoyRow) rows.splice(2, 0, [`Hoy, hasta las ${hv?.ultSync ?? '—'}`, `${fmtE(hoyRow.imp)} (provisional)`, 'flat']);
    }
    if (hotel.ob) rows.push(['⚠ Overbooking', `${hotel.ob} día(s) en los próximos 30`, 'bad']);
    const notaPms = (hotel.pms === 'avalon' || hotel.pms === 'navision')
      ? ` Ojo: ${PMS_LBL[hotel.pms]} solo publica reservas — su producción es la de reservas vigentes, sin folio ni cierre contable.`
      : '';
    /* 🔴 «¿Cómo va Mirage?» pedía el desglose del día, no solo el titular: es
       lo que dirección mira en Facturación del día. Va DEBAJO del resumen, en
       secciones, para que se lea igual que el informe. Solo Noray tiene folio;
       para el resto el resumen se queda como está, sin fingir un desglose. */
    let secciones: IaReply['secciones'];
    let piePdv = '';
    if (hotel.pms === 'noray' && hotel.id != null) {
      const hd = await api.hotelDia(hotel.id);
      if (hd.ok && hd.dato.hayDatos) {
        const d = hd.dato;
        const bloque = (t: string, arr: typeof d.recepcion, sub: { sinIva: number; lineas: number }) =>
          arr.length ? [{
            t, sub: `${fmt(sub.lineas)} líneas`,
            filas: arr.slice(0, 9).map((p): IaReply['rows'][0] => [p.punto, fmtE(p.sinIva), 'flat']),
            total: ['', fmtE(sub.sinIva)] as [string, string],
          }] : [];
        secciones = [
          ...bloque('Recepción', d.recepcion, d.subRecepcion),
          ...bloque('POS / F&B', d.pos, d.subPos),
        ];
        piePdv = ` Desglose del día ${d.esHoy ? 'en curso' : ddmm(d.fecha)}${d.ultSync ? `, hasta las ${d.ultSync}` : ''}: neto sin IVA.`;
      }
    }
    return {
      intencion: 'hotel',
      txt: `${hotel.n} (${PMS_LBL[hotel.pms] ?? hotel.pms}):`,
      rows, secciones,
      foot: 'Ficha completa en la pestaña Hoteles, tocando el hotel.' + notaPms + piePdv
          + (hotel.pms === 'noray' ? ' Pídeme «la producción de ayer» para el desglose por cuenta.' : ''),
    };
  }

  /* ── Tortugona / premios ────────────────────────────────────────────── */
  if (hay(q, 'tortugona', 'premio', 'premios noa', 'podio', 'gano', 'ganador', 'caracolas', 'medalla')) {
    if (!premio) return sinDatos;
    if (!premio.disponible) {
      return {
        txt: premio.pendiente
          ? 'Todavía no hay ganadores.'
          : 'Los Premios Noa no tienen podio ahora mismo.',
        rows: [], intencion: 'premios-noa',
        foot: premio.motivo ?? 'El podio aparece aquí en cuanto se publica la edición en Hotelos.',
      };
    }
    return {
      intencion: 'premios-noa',
      txt: `${premio.premio ?? 'Premios Noa'} de ${MESES[premio.mes ?? 0]} ${premio.anio ?? ''} — oro, plata y bronce:`,
      tortugonas: true,   // el chat pinta la Tortugona oro/plata/bronce (jamás medallas)
      rows: (premio.podio ?? []).map((p): IaReply['rows'][0] => [
        p.n.replace(/^Ona /i, '') + (p.mio ? ' · el tuyo' : ''),
        p.detalle ?? (p.valor != null ? fmtDec(p.valor) : `nº ${p.puesto}`),
        'good',
      ]),
      foot: `Edición publicada en Hotelos${premio.publicadoPor ? ` por ${premio.publicadoPor}` : ''}`
          + `. Caracolas: ${(premio.podio ?? []).map((p) => `${p.caracolas ?? 0}`).join(' · ')} 🐚.`,
    };
  }

  /* ── alertas / overbooking ──────────────────────────────────────────── */
  if (hay(q, 'overbooking', 'alerta', 'aviso', 'problema', 'riesgo', 'sobreventa')) {
    if (!alertas.length) return { txt: 'Sin excepciones ahora mismo. Todo tranquilo 🌊', rows: [], foot: '' };
    const ob = alertas.filter((a) => a.ic === 'bed');
    const otras = alertas.filter((a) => a.ic !== 'bed');
    const rows: IaReply['rows'] = [
      ...ob.slice(0, 4).map((a): IaReply['rows'][0] => [a.t.replace('Overbooking en ', '⚠ '), a.detalle?.[0] ? `${a.detalle[0][0]} · ${a.detalle[0][1]}` : a.m, 'bad']),
      ...otras.slice(0, 2).map((a): IaReply['rows'][0] => [a.t, a.tm, 'flat']),
    ];
    return {
      txt: `Hay ${alertas.length} alerta(s) activa(s)${ob.length ? `, ${ob.length} de overbooking` : ''}:`,
      rows,
      foot: 'El detalle día a día está en la pestaña Alertas (toca para expandir).',
    };
  }

  /* ── ranking mejores / peores ───────────────────────────────────────── */
  if (hay(q, 'mejor', 'top', 'ranking', 'primero', 'lidera')) {
    if (!hoteles.length) return sinDatos;
    return {
      txt: 'Top 3 del mes por producción:',
      rows: hoteles.slice(0, 3).map((h, i): IaReply['rows'][0] => [`${i + 1}. ${h.n.replace(/^Ona /i, '')}`, fmtK(h.mes), 'good']),
      foot: 'Ranking completo en la pestaña Hoteles. (El premio oficial son los Premios Noa, sobre mes publicado.)',
    };
  }
  if (hay(q, 'peor', 'ultimo', 'cola', 'flojo', 'menos produce')) {
    if (!hoteles.length) return sinDatos;
    return {
      txt: 'Los 3 de menor producción del mes:',
      rows: [...hoteles].slice(-3).reverse().map((h): IaReply['rows'][0] => [h.n.replace(/^Ona /i, ''), fmtK(h.mes), 'flat']),
      foot: 'Ojo: comparar hoteles de tamaños distintos por producción absoluta tiene trampa — para mérito, los Premios Noa (vs budget).',
    };
  }

  /* ── ocupación (grupo) ──────────────────────────────────────────────── */
  if (hay(q, 'ocupacion', 'lleno', 'llenos', 'duermen', 'habitaciones vendidas')) {
    if (!brief || brief.occ == null) return sinDatos;
    return {
      txt: `Ocupación de hoy en el grupo: ${fmtDec(brief.occ)}% (${brief.hoteles} hoteles, 4 PMS).`,
      rows: filasPorPms((brief.occPorPms ?? []).map((o) => ({ pms: o.pms, v: `${fmtDec(o.media)}%`, extra: `${o.hoteles} hot.` }))),
      foot: 'Cada PMS con su propia doctrina de «ocupada» — la misma rejilla que Hotelos.',
    };
  }

  /* ── ADR / precio medio ─────────────────────────────────────────────── */
  if (hay(q, 'adr', 'precio medio', 'tarifa media', 'arr')) {
    if (!brief || brief.adr == null) {
      return { txt: 'El ADR del mes aún no está disponible en esta carga.', rows: [], foot: 'Vuelve a intentarlo en unos segundos.' };
    }
    return {
      txt: `ADR del mes (grupo): ${fmtDec(brief.adr)} € — alojamiento neto ÷ noches.`,
      rows: filasPorPms((brief.adrPorPms ?? []).map((a) => ({ pms: a.pms, v: `${fmtDec(a.adr)} €`, extra: `${fmt(a.rn)} noches` }))),
      foot: 'Navision sale algo alto: su espejo no separa la pensión (declarado).',
    };
  }

  /* ── budget ─────────────────────────────────────────────────────────── */
  if (hay(q, 'budget', 'presupuesto', 'objetivo', 'plan')) {
    const top = premio?.podio?.[0];
    return {
      txt: 'El % de producción vs budget del grupo está en camino (falta definir la ref comparable).'
         + (top ? ' Lo que sí hay es el podio publicado de los Premios Noa:' : ''),
      rows: top ? [[`🥇 ${top.n.replace(/^Ona /i, '')}`, top.detalle ?? (top.valor != null ? fmtDec(top.valor) : `nº ${top.puesto}`), 'good']] : [],
      foot: 'Pregúntame «¿quién ganó la Tortugona?» para el podio completo.',
    };
  }

  /* ── producción del mes ─────────────────────────────────────────────── */
  if (hay(q, 'mes', 'llevamos', 'acumulado', 'agosto', 'este mes')) {
    if (!brief) return sinDatos;
    return {
      txt: `Producción del mes (neto sin IVA): ${fmtM(brief.prodMes)}${brief.fecha ? `, hasta el ${ddmm(brief.fecha)}` : ''}.`,
      rows: filasPorPms((brief.fuentes ?? []).filter((f) => f.mes != null).map((f) => ({ pms: f.pms, v: fmtM(f.mes as number) }))),
      foot: 'Mismo criterio que tus pantallas de Producción — validado al céntimo.',
    };
  }

  /* ── producción de ayer / cuánto hicimos (comodín de producción) ────── */
  if (hay(q, 'ayer', 'produccion', 'produjo', 'facturamos', 'facturacion', 'hicimos', 'vendimos', 'cuanto')) {
    if (!brief) return sinDatos;
    return {
      txt: `Ayer${brief.fecha ? ` (${ddmm(brief.fecha)})` : ''} el grupo produjo ${fmtE(brief.prodDia)} netos, con ${brief.hoteles} hoteles reportando en ${brief.pms} PMS.`,
      rows: filasPorPms((brief.fuentes ?? []).filter((f) => f.dia != null).map((f) => ({ pms: f.pms, v: fmtE(f.dia as number), extra: `${f.hoteles} hot.` }))),
      foot: '«Ayer» = último día completo (con el folio Noray volcado).',
    };
  }

  /* ── fuentes / fiabilidad ───────────────────────────────────────────── */
  if (hay(q, 'fuente', 'de donde', 'fiable', 'iva', 'como calculas', 'espejo')) {
    return {
      txt: 'Todo sale de los espejos de Hotelos, en neto SIN IVA:',
      rows: [
        ['Noray', 'movimientos de conceptos (folio)', 'flat'],
        ['Hoy / puntos de venta', 'folios vivos de Facturación Noray', 'flat'],
        ['OnaSystem', 'espejo B1010 de producción', 'flat'],
        ['Navision / Avalon', 'diario de reservas a base', 'flat'],
        ['Ocupación', 'rejilla 4 PMS (cada uno su doctrina)', 'flat'],
        ['Premios Noa', 'foto publicada de CG vs budget', 'flat'],
      ],
      foot: 'Distintivo ● LIVE = datos reales; DEMO = simulados y declarados.',
    };
  }

  /* ── ¿se lo han enseñado? ────────────────────────────────────────────
     🔴 AQUÍ Y NO ANTES. Lo enseñado se consulta cuando ya han fallado TODAS
     las intenciones que calculan: un texto escrito hace tres meses jamás debe
     tapar la cifra de hoy. Y solo contesta si el parecido es alto — el
     servidor exige que casen dos palabras con contenido y el 60 % de las de
     la pregunta enseñada. Contestar algo que no venía a cuento es peor que
     decir «no lo sé». */
  const ensenada = await api.noaRespuesta(q);
  if (ensenada) {
    return {
      txt: ensenada.txt, rows: [],
      foot: ensenada.pie ?? 'Esto me lo enseñaron desde Sistemas.',
      intencion: 'ensenada',
    };
  }

  /* ── fallback honesto pero con naturalidad (rota la frase) ──────────── */
  const variantes = [
    'Esa no la tengo a mano todavía. Sí te puedo contar la producción (incluida la de hoy hasta ahora), la ocupación, el ADR, los puntos de venta —súper, bares, restaurantes—, cualquier hotel, las alertas o los Premios Noa.',
    'Ahí no llego, de momento 😉 Donde sí voy sobrada: producción de ayer y del día en curso, ocupación, ADR, supermercados y bares, hoteles concretos, alertas y los Premios Noa.',
    'Eso se me escapa por ahora — lo apunto. Mientras tanto pregúntame por el día de hoy, la producción, la ocupación, el ADR, un punto de venta o el podio de los Premios Noa.',
  ];
  return {
    txt: variantes[q.length % variantes.length],
    rows: [],
    foot: 'Prueba: «¿cómo van los supermercados?» o «¿cuánto llevamos hoy?»',
    noSupe: true,           // al cuaderno: esta es de las que hay que enseñarle
  };
}

/* Sugerencias dinámicas (chips) — usan nombres reales cuando hay datos. */
export function noaSugerencias(ctx: NoaCtx): string[] {
  const h1 = ctx.hoteles[0]?.n.replace(/^Ona /i, '');
  return [
    '¿Cuánto llevamos hoy?',
    '¿Cómo van los supermercados?',
    '¿Cuánto produjo el grupo ayer?',
    h1 ? `¿Cómo va ${h1}?` : '¿Cómo va Don Juan?',
    '¿Quién ganó los Premios Noa?',
    '¿Hay overbooking?',
    '¿Cómo va la ocupación?',
    '¿Qué sabes hacer?',
  ];
}
